<template>
  <div class="def-etym">
    <p class="def-etym-label">{{ STRINGS.definitions.etymology }}</p>
    <p class="def-etym-body">
      {{ expanded ? text : trimmed
      }}<button
        v-if="isTrimmed"
        type="button"
        class="def-etym-more"
        data-testid="etymology-toggle"
        @click="expanded = !expanded"
      >
        {{ expanded ? STRINGS.definitions.etymologyLess : STRINGS.definitions.etymologyMore }}
      </button>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import STRINGS from '@/constants/strings';

const props = defineProps<{
  text: string;
}>();

// Wiktextract etymologies can run to a paragraph of nested origins. We only need
// the proximate story, so we cap at ~150 chars and cut back to the nearest clause
// boundary (sentence → em-dash → semicolon → comma) so a line never ends
// mid-word or mid-language. The full text stays available via the More toggle.
function trimEtymology(text: string, max = 150): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const boundary = Math.max(
    slice.lastIndexOf('. '),
    slice.lastIndexOf('— '),
    slice.lastIndexOf('; '),
    slice.lastIndexOf(', ')
  );
  const cut = boundary > max * 0.5 ? slice.slice(0, boundary) : slice.replace(/\s+\S*$/, '');
  return cut.replace(/[\s.,;—]+$/, '') + '…';
}

const expanded = ref(false);
const trimmed = computed(() => trimEtymology(props.text));
const isTrimmed = computed(() => trimmed.value !== props.text);
</script>

<style scoped>
.def-etym {
  margin-top: 0.25rem;
  padding-top: 1rem;
  border-top: 2px solid var(--color-arena-border);
}

.def-etym-label {
  margin: 0 0 0.375rem;
  font-family: var(--font-sans);
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-arena-text-muted);
}

.def-etym-body {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 0.84375rem;
  font-style: italic;
  line-height: 1.55;
  color: var(--color-arena-text-dim);
}

.def-etym-more {
  margin-left: 0.375rem;
  padding: 0;
  font-family: var(--font-sans);
  font-style: normal;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-energy-primary);
  background: none;
  border: none;
  cursor: pointer;
}

.def-etym-more:hover,
.def-etym-more:focus-visible {
  text-decoration: underline;
}
</style>
